import { Component, effect, inject, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { InitialConfigFileOption } from '@core/models';
import { TranslateModule } from '@ngx-translate/core';
import { DeviceStore } from '@root/app/store/device.store';
import { DeviceService } from '@core/services';
import { concatMap, first, interval, switchMap } from 'rxjs';

@Component({
  selector: 'xemex-change-initial-configuration-dialog',
  imports: [
    MatIconModule,
    MatButtonModule,
    MatDialogModule,
    MatProgressSpinnerModule,
    FormsModule,
    MatFormFieldModule,
    MatSelectModule,
    TranslateModule,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './change-initial-configuration-dialog.component.html',
})
export class ChangeInitialConfigurationDialogComponent implements OnDestroy {
  readonly deviceStore = inject(DeviceStore);
  readonly dialogRef = inject(MatDialogRef<ChangeInitialConfigurationDialogComponent>);
  readonly data = inject(MAT_DIALOG_DATA) as {
    deviceId: string;
    selectedFileId: number | undefined;
  };
  private readonly deviceService = inject(DeviceService);

  selectedFileId: number | undefined;
  initialConfigurations: InitialConfigFileOption[] = [];
  isLoading = false;
  isSending = false;
  sendError: string | null = null;
  succeeded = false;
  countdown = 3;
  private countdownTimer: ReturnType<typeof setInterval> | null = null;

  constructor() {
    effect(() => {
      this.initialConfigurations =
        this.deviceStore.initialConfigFiles()?.initialConfigurations ?? [];
      this.isLoading = this.deviceStore.isLoadingInitialConfigFiles();
    });
    this.deviceStore.getInitialConfigFilesForDeviceMethod(this.data.deviceId);
    this.selectedFileId = this.data.selectedFileId;
  }

  ngOnDestroy(): void {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
    }
  }

  confirm(): void {
    if (this.selectedFileId == null) return;

    this.isSending = true;
    this.sendError = null;

    const deviceId = this.data.deviceId;
    const fileId = this.selectedFileId;

    this.deviceService
      .sendInitialConfigToDevice(deviceId, fileId)
      .pipe(
        switchMap(({ initialConfigDispatchId }) =>
          interval(1000).pipe(
            concatMap(() =>
              this.deviceService.getInitialConfigDispatchStatus(initialConfigDispatchId),
            ),
            first(data => !['Pending', 'In Progress'].includes(data.status)),
          ),
        ),
      )
      .subscribe({
        next: data => {
          this.isSending = false;
          this.succeeded = data.status == 'Success';
          this.sendError = data.error;
          if (this.succeeded) {
            this.deviceStore.getChannelConfigurationMethod(deviceId);
            this.startCountdown();
          }
        },
        error: err => {
          this.isSending = false;
          this.sendError = err?.error?.message ?? err?.message ?? 'An unexpected error occurred';
        },
      });
  }

  private startCountdown(): void {
    this.countdown = 3;
    const timer = setInterval(() => {
      this.countdown--;
      if (this.countdown <= 0) {
        clearInterval(timer);
        this.countdownTimer = null;
        this.dialogRef.close(null);
      }
    }, 1000);
    this.countdownTimer = timer;
  }
}
